import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Route, X } from 'lucide-react';
import type { Subdivision } from 'api/referentialsApi';
import { cn } from 'lib/utils';

/**
 * The remove button says which zone it drops, in the country's own word — "Retirer le
 * département" reads wrong of a province. Local rather than beside `allSubdivisionsLabel`: this
 * screen is its only consumer, and the keys must stay literal for the i18n static guard.
 */
const removeZoneLabel = (t: TFunction, code: string, name: string): string => {
  switch (code) {
    case 'department':
      return t('Remove the department {{name}}', { name });
    case 'province':
      return t('Remove the province {{name}}', { name });
    case 'county':
      return t('Remove the county {{name}}', { name });
    default:
      return t('Remove the area {{name}}', { name });
  }
};

interface ZoneChipProps {
  zone: Subdivision;
  secondary?: boolean;
  readOnly: boolean;
  onRemove: () => void;
}

/** One compact chip per zone, wrapping in a row — amber and a route icon mark the secondary one (F18). */
const ZoneChip = ({ zone, secondary = false, readOnly, onRemove }: ZoneChipProps) => {
  const { t } = useTranslation();
  return (
    <li
      className={cn(
        'inline-flex h-6 items-center gap-1 rounded-md px-2 text-[12.5px] font-medium',
        secondary
          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200'
          : 'bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-50'
      )}
    >
      {secondary && <Route className="h-3 w-3 shrink-0" aria-hidden="true" />}
      <span className="tabular-nums">{zone.code}</span>
      <span aria-hidden="true">·</span>
      <span>{zone.name}</span>
      {!readOnly && (
        <button
          type="button"
          aria-label={removeZoneLabel(t, zone.country_subdivision_type, zone.name)}
          onClick={onRemove}
          className="-mr-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm opacity-60 transition hover:bg-rose-100 hover:text-rose-600 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:hover:bg-rose-950/50 dark:hover:text-rose-400 dark:focus-visible:ring-zinc-100"
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
    </li>
  );
};

export default ZoneChip;
